import { PersistentStore } from './Store';
import { Database } from '.';
import Media from 'jsmediatags';
import { TagType } from 'jsmediatags/types';

export interface SongEntry {
  path: string[];
  title: string;
  artist: string;
  album: string;
  duration: number;
}

export interface AlbumEntry {
  title: string;
  artist: string;
  songs: SongEntry[];
}

export interface ArtistEntry {
  title: string;
  albums: AlbumEntry[];
}

export interface MusicStoreState extends Object {
  artists: { [title: string]: ArtistEntry };
  albums: { [title: string]: AlbumEntry };
  songs: { [title: string]: SongEntry };
}

interface ScannerPosition {
  ancestors: string[];
  handle: FileSystemDirectoryHandle;
}

function ReadMP3Tags(file: File): Promise<TagType> {
  return new Promise((resolve, reject) => {
    new Media.Reader(file)
      .setTagsToRead([
        'artist',
        'album',
        'title',
        'track',
        'year'
      ])
      .read({
        onSuccess: resolve,
        onError: reject
      });
  });
}

function GetAudioLength(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio(URL.createObjectURL(file));
    audio.preload = 'metadata';
    audio.addEventListener('error', reject);
    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration);
    });
  });
}

export interface MusicScanProgress {
  count: number;
  total: number;
}

export default class MusicStore extends PersistentStore<MusicStoreState> {
  protected data(): MusicStoreState {
    return {
      artists: {},
      albums: {},
      songs: {}
    };
  }

  static Upgrade(db: Database, oldVersion: number, newVersion: number): void {
    if (newVersion === 1) {
      const store = db.createObjectStore('songs', {
        keyPath: 'path'
      });

      store.createIndex('title', 'title');
      store.createIndex('album', 'album');
      store.createIndex('artist', 'artist');
      store.createIndex('path', 'path', { multiEntry: false });
    }
  }

  async load() {
    const songs = await this.db.getAll('songs');

    console.log('Loading music store, %s songs', songs.length);

    for (const song of songs) {
      if (!Object.prototype.hasOwnProperty.call(this.state.albums, song.album)) {
        this.state.albums[song.album] = {
          artist: song.artist,
          title: song.album,
          songs: []
        };
      }
      const album = this.state.albums[song.album];
      album.songs.push(song);

      if (!Object.prototype.hasOwnProperty.call(this.state.artists, song.artist)) {
        this.state.artists[song.artist] = {
          title: song.artist,
          albums: []
        };
      }
      const artist = this.state.artists[song.artist];
      if (!artist.albums.includes(album)) {
        artist.albums.push(album);
      }
    }
  }

  async scan(root: FileSystemDirectoryHandle, cb?: (progress: MusicScanProgress) => void) {
    const queue: ScannerPosition[] = [{
      ancestors: [],
      handle: root
    }];

    const progress: MusicScanProgress = {
      count: 0,
      total: 0
    };

    const todo: Array<{ancestors: string[]; handle: FileSystemFileHandle }> = [];

    while (queue.length) {
      const at = queue.shift();

      const nextAncestors = at.ancestors.concat(at.handle.name);

      for await (const child of at.handle.getEntries()) {
        if (child.isDirectory) {
          queue.push({
            ancestors: nextAncestors,
            handle: child
          });
        } else if (child.isFile) {
          todo.push({
            ancestors: at.ancestors,
            handle: child
          });
        }
      }
    }

    progress.total = todo.length;

    for (const { handle, ancestors } of todo) {
      console.log('Got file', handle.name, 'at', ancestors);
      try {
        const file = await handle.getFile();
        const { tags } = await ReadMP3Tags(file);

        tags.artist = tags.artist || 'Unknown Artist';
        tags.album = tags.album || 'Unknown Album';

        const songEntry = {
          artist: tags.artist,
          album: tags.album,
          title: tags.title,
          path: [...ancestors, handle.name],
          duration: await GetAudioLength(file)
        };

        // Try and add to the database if we don't have it
        await this.db.put('songs', songEntry);
      } catch (e) {
        console.log('  but not a tagged file', e);
      }

      progress.count++;
      if (cb) {
        cb(progress);
      }
    }

    // Do the load
    await this.load();

    console.log('Thats everything folks!');
  }
}
